import React from 'react';
import type { Icon } from '@phosphor-icons/react';
import { tonFarben, type Ton } from './ton';

/** Chip / Badge (README §4.4: 10–12 px, 800; Radius 10–14). */
export interface ChipProps {
  ton?: Ton;
  /** xs = 20 px (Badges im Widget), s = 24 px, m = 28 px (Filter-Chips). */
  groesse?: 'xs' | 's' | 'm';
  icon?: Icon;
  /** Farbpunkt vor dem Text (z. B. „Portal aktiv“). */
  punkt?: boolean;
  title?: string;
  className?: string;
  children: React.ReactNode;
}

const MASS = {
  xs: { h: 20, px: 8, fs: 10, r: 10, icon: 11 },
  s: { h: 24, px: 10, fs: 11, r: 12, icon: 12 },
  m: { h: 28, px: 11, fs: 12, r: 14, icon: 13 },
} as const;

export function Chip({ ton = 'neutral', groesse = 's', icon: IconKomp, punkt, title, className = '', children }: ChipProps) {
  const { bg, fg } = tonFarben(ton);
  const m = MASS[groesse];
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap ${className}`}
      style={{ height: m.h, padding: `0 ${m.px}px`, borderRadius: m.r, gap: 4, fontSize: m.fs, fontWeight: 800, background: bg, color: fg }}
    >
      {punkt && <span style={{ width: 6, height: 6, borderRadius: '50%', background: fg, flexShrink: 0 }} />}
      {IconKomp && <IconKomp weight="fill" size={m.icon} />}
      {children}
    </span>
  );
}

/** Status-Pille: Chip mit Punkt, feste Zuordnung Status → Ton. */
export type ImmoStatus = 'vermietet' | 'leerstand' | 'rueckstand' | 'bezahlt' | 'offen' | 'aktiv' | 'eingeladen' | 'gesperrt' | 'inaktiv';

const STATUS: Record<ImmoStatus, { ton: Ton; label: string }> = {
  vermietet: { ton: 'neutral', label: 'Vermietet' },
  leerstand: { ton: 'warn', label: 'Leerstand' },
  rueckstand: { ton: 'bad', label: 'Rückstand' },
  bezahlt: { ton: 'ok', label: 'Bezahlt' },
  offen: { ton: 'neutral', label: 'Offen' },
  aktiv: { ton: 'ok', label: 'Aktiv' },
  eingeladen: { ton: 'warn', label: 'Eingeladen' },
  gesperrt: { ton: 'bad', label: 'Gesperrt' },
  inaktiv: { ton: 'neutral', label: 'Inaktiv' },
};

export function StatusPill({ status, label, groesse = 'xs' }: { status: ImmoStatus; label?: string; groesse?: ChipProps['groesse'] }) {
  const s = STATUS[status];
  return <Chip ton={s.ton} groesse={groesse} punkt>{label ?? s.label}</Chip>;
}
